import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import {
  Microscope, Search, RefreshCw, Download, ChevronDown, ChevronRight,
  Flame, ShieldAlert, Dice5, Sparkles, Layers,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const VERDICT_META = {
  predictable_missed: {
    label: "Predictable · missed", tone: "rose", icon: ShieldAlert,
    hint: "Signals (form / H2H) pointed the other way — engine ignored them.",
  },
  unexpected_shock: {
    label: "In-game shock", tone: "amber", icon: Flame,
    hint: "Red card, penalty, own goal, or an early goal burst broke the model.",
  },
  coin_flip_variance: {
    label: "Coin-flip variance", tone: "neutral", icon: Dice5,
    hint: "Confidence sat in the 48-62% band — variance expected.",
  },
  outlier_upset: {
    label: "Outlier upset", tone: "purple", icon: Sparkles,
    hint: "Big form gap flipped — rare mathematical upset.",
  },
};

const TONE = {
  rose: "bg-rose-500/10 border-rose-500/40 text-rose-200",
  amber: "bg-amber-500/10 border-amber-500/40 text-amber-200",
  neutral: "bg-neutral-500/10 border-neutral-500/40 text-neutral-200",
  purple: "bg-purple-500/10 border-purple-500/40 text-purple-200",
  blue: "bg-blue-500/10 border-blue-500/40 text-blue-200",
};

const VerdictBadge = ({ verdict }) => {
  const meta = VERDICT_META[verdict] || VERDICT_META.coin_flip_variance;
  const Icon = meta.icon;
  return (
    <span
      title={meta.hint}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] uppercase tracking-widest font-bold ${TONE[meta.tone]}`}
      data-testid={`miss-verdict-${verdict}`}
    >
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
};

const CauseChip = ({ cause }) => (
  <span
    className="inline-block px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-[10px] text-amber-100/80 mr-1 mb-1"
    data-testid={`miss-cause-chip-${cause}`}
  >
    {cause.replace(/_/g, " ")}
  </span>
);

const MissRow = ({ miss, expanded, onToggle, onExport }) => {
  const kickoff = (miss.kickoff || "").slice(0, 16).replace("T", " ");
  return (
    <div
      className="border-b border-amber-500/10 last:border-b-0"
      data-testid="miss-row"
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 py-3 px-3 text-left hover:bg-amber-500/5 transition-colors"
        data-testid={`miss-row-toggle-${miss.fixture_id}`}
      >
        {expanded ? <ChevronDown className="w-4 h-4 text-amber-400" />
                   : <ChevronRight className="w-4 h-4 text-amber-500/70" />}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-amber-50 truncate">
            {miss.home_name} <span className="text-amber-500/60">vs</span> {miss.away_name}
            <span className="ml-2 text-amber-500/50 text-[11px] font-normal">
              {kickoff} · {miss.league_name}
            </span>
          </div>
          <div className="text-[11px] text-amber-300/70 mt-1">
            <span className="uppercase tracking-widest">
              {miss.market_type} · {miss.prediction}
            </span>
            <span className="ml-2 text-amber-500/70">
              @ {miss.confidence}% → actual {miss.actual_home}-{miss.actual_away}
            </span>
          </div>
        </div>
        <VerdictBadge verdict={miss.predictability_verdict} />
      </button>
      {expanded && (
        <div className="px-6 pb-4 pt-1 text-sm text-amber-100/80 space-y-3 bg-black/30">
          <p className="text-[12px] leading-relaxed text-amber-100/90">{miss.narrative}</p>

          <div className="grid md:grid-cols-3 gap-3 text-[11px]">
            <div className="rounded border border-amber-500/20 p-2 bg-black/40">
              <div className="text-amber-500/70 uppercase tracking-widest text-[10px] mb-1">
                Home form ({miss.home_form?.gp || 0} games)
              </div>
              <div className="text-amber-100">
                {miss.home_form?.sequence || "—"} · PPG {miss.home_form?.ppg ?? "—"}
              </div>
              <div className="text-amber-100/70">
                GF/GA {miss.home_form?.gf_pm ?? "—"} / {miss.home_form?.ga_pm ?? "—"}
              </div>
            </div>
            <div className="rounded border border-amber-500/20 p-2 bg-black/40">
              <div className="text-amber-500/70 uppercase tracking-widest text-[10px] mb-1">
                Away form ({miss.away_form?.gp || 0} games)
              </div>
              <div className="text-amber-100">
                {miss.away_form?.sequence || "—"} · PPG {miss.away_form?.ppg ?? "—"}
              </div>
              <div className="text-amber-100/70">
                GF/GA {miss.away_form?.gf_pm ?? "—"} / {miss.away_form?.ga_pm ?? "—"}
              </div>
            </div>
            <div className="rounded border border-amber-500/20 p-2 bg-black/40">
              <div className="text-amber-500/70 uppercase tracking-widest text-[10px] mb-1">
                H2H (last {miss.h2h?.gp || 0})
              </div>
              <div className="text-amber-100">
                {miss.h2h?.home_wins ?? "—"} W · {miss.h2h?.draws ?? "—"} D · {miss.h2h?.away_wins ?? "—"} L
              </div>
              <div className="text-amber-100/70">
                BTTS {miss.h2h?.btts_pct ?? "—"}% · O2.5 {miss.h2h?.over_2_5_pct ?? "—"}%
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3 text-[11px]">
            <div className="rounded border border-amber-500/20 p-2 bg-black/40">
              <div className="text-amber-500/70 uppercase tracking-widest text-[10px] mb-1">
                3-season league study
              </div>
              <div className="text-amber-100">
                {miss.league_density_3_seasons?.fixtures_3y ?? 0} fixtures ·{" "}
                {miss.league_density_3_seasons?.seasons ?? 0}/3 seasons pulled
              </div>
              <div className="text-amber-100/70">
                API seasons: {(miss.league_density_3_seasons?.api_seasons_pulled || []).join(", ") || "—"}
              </div>
              <div className="text-amber-100/70">
                Market-settled samples: {miss.league_density_3_seasons?.market_settled ?? 0}
              </div>
            </div>
            <div className="rounded border border-amber-500/20 p-2 bg-black/40">
              <div className="text-amber-500/70 uppercase tracking-widest text-[10px] mb-1">
                Event shocks
              </div>
              {miss.event_summary?.has_events ? (
                <>
                  <div className="text-amber-100">
                    Red {miss.event_summary?.red_cards ?? 0}
                    {miss.event_summary?.first_red_min ? ` @ ${miss.event_summary.first_red_min}'` : ""}
                    · Pen {miss.event_summary?.penalties ?? 0}
                    · OG {miss.event_summary?.own_goals ?? 0}
                  </div>
                  <div className="text-amber-100/70">
                    Early goals (&le;15&apos;): {miss.event_summary?.early_goals_before_15 ?? 0}
                  </div>
                </>
              ) : (
                <div className="text-amber-500/60">No event data pulled.</div>
              )}
            </div>
          </div>

          <div>
            <div className="text-amber-500/70 uppercase tracking-widest text-[10px] mb-1">
              Root causes
            </div>
            <div>{(miss.root_causes || []).map((c) => <CauseChip key={c} cause={c} />)}</div>
          </div>

          <div className="flex items-center justify-end pt-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onExport(miss.pick_signature)}
              data-testid={`miss-export-${miss.fixture_id}`}
              className="text-amber-200 border-amber-500/40 hover:bg-amber-500/10"
            >
              <Download className="w-3 h-3 mr-2" /> Download JSON
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const RollupCard = ({ rollups }) => {
  const global = rollups?.global_root_causes || {};
  const total = rollups?.total_misses || 0;
  const entries = Object.entries(global).slice(0, 10);
  return (
    <Card className="bg-gradient-to-br from-black to-neutral-950 border-amber-500/30" data-testid="miss-rollup-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-bold text-amber-200 uppercase tracking-widest">
            Root-cause rollup
          </div>
          <div className="text-[11px] text-amber-500/70">
            {total} misses · {rollups?.pairs ?? 0} league/market pairs
          </div>
        </div>
        {entries.length === 0 ? (
          <div className="text-xs text-amber-500/60">
            No rollups yet. Run the analyzer to build the first snapshot.
          </div>
        ) : (
          <div className="space-y-1">
            {entries.map(([cause, count]) => {
              const pct = total > 0 ? Math.min(100, (count / total) * 100) : 0;
              return (
                <div key={cause} className="text-xs" data-testid={`rollup-row-${cause}`}>
                  <div className="flex items-center justify-between text-amber-100/80">
                    <span className="uppercase tracking-widest text-[10px]">
                      {cause.replace(/_/g, " ")}
                    </span>
                    <span className="text-amber-300 font-bold">{count}</span>
                  </div>
                  <div className="h-1.5 bg-white/5 rounded overflow-hidden">
                    <div
                      className="h-full bg-amber-400/70"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const LearningsCard = ({ learnings, pitfalls }) => {
  const l = learnings?.rows || [];
  const p = pitfalls?.rows || [];
  return (
    <Card className="bg-gradient-to-br from-black to-neutral-950 border-amber-500/30" data-testid="miss-learnings-card">
      <CardContent className="p-4">
        <div className="text-sm font-bold text-amber-200 uppercase tracking-widest mb-2">
          Applied learnings & pitfalls
        </div>
        <div className="text-[11px] text-amber-500/70 mb-3">
          {l.length} corrections applied · {p.length} active pitfalls tracked
        </div>
        {p.length === 0 && l.length === 0 ? (
          <div className="text-xs text-amber-500/60">
            No learnings yet. Nightly cron @ 04:30 UTC (or run manually).
          </div>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {p.map((row, i) => (
              <div
                key={`p-${i}`}
                className="text-[11px] rounded border border-amber-500/20 bg-black/30 p-2"
                data-testid={`pitfall-row-${i}`}
              >
                <div className="text-amber-300 font-semibold uppercase tracking-widest">
                  {row.cause?.replace(/_/g, " ")}
                </div>
                <div className="text-amber-100/80">
                  League {row.league_id} · {row.market_type} · {row.misses_recorded} misses
                </div>
                <div className="text-amber-500/70 italic">{row.notes}</div>
                <div className="text-[10px] text-emerald-300 mt-1">→ {row.action}</div>
              </div>
            ))}
            {l.slice(0, 15).map((row, i) => (
              <div
                key={`l-${i}`}
                className="text-[11px] rounded border border-emerald-500/20 bg-black/30 p-2"
                data-testid={`learning-row-${i}`}
              >
                <div className="text-emerald-300 font-semibold uppercase tracking-widest">
                  {row.action?.replace(/_/g, " ")}
                </div>
                <div className="text-amber-100/80">
                  League {row.league_id} · {row.market_type} · {row.misses} misses
                </div>
                <div className="text-[10px] text-amber-500/60">
                  {(row.at || "").slice(0, 19).replace("T", " ")}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function MissAnalyzerPanel({ token, isAdmin }) {
  const [misses, setMisses] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [days, setDays] = useState(30);
  const [verdict, setVerdict] = useState("");
  const [loading, setLoading] = useState(false);
  const [rollups, setRollups] = useState(null);
  const [learnings, setLearnings] = useState(null);
  const [pitfalls, setPitfalls] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");

  const headers = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [listR, rollR, learnR, pitR] = await Promise.all([
        axios.get(`${API}/api/gold/miss-analyzer/list`,
          { headers, params: { days, limit: 100, verdict: verdict || undefined } }),
        axios.get(`${API}/api/gold/miss-analyzer/rollups`, { headers }),
        axios.get(`${API}/api/gold/miss-analyzer/learnings`, { headers, params: { limit: 20 } }),
        axios.get(`${API}/api/gold/miss-analyzer/pitfalls`, { headers }),
      ]);
      setMisses(listR.data?.rows || []);
      setRollups(rollR.data || {});
      setLearnings(learnR.data || {});
      setPitfalls(pitR.data || {});
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Load failed");
    } finally {
      setLoading(false);
    }
  }, [headers, days, verdict]);

  const runAnalyzer = useCallback(async () => {
    setRunning(true);
    setError("");
    try {
      await axios.post(
        `${API}/api/gold/miss-analyzer/run`,
        {},
        { headers, params: { days: 365, limit: 500, force: false } },
      );
      // Poll status every 3s while the background job is running.
      let done = false;
      let ticks = 0;
      while (!done && ticks < 120) {
        await new Promise((r) => setTimeout(r, 3000));
        const s = await axios.get(`${API}/api/gold/miss-analyzer/status`, { headers });
        if (!s.data?.running) done = true;
        ticks += 1;
      }
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Analyzer run failed");
    } finally {
      setRunning(false);
    }
  }, [headers, load]);

  const exportOne = useCallback(async (sig) => {
    try {
      const r = await axios.get(
        `${API}/api/gold/miss-analyzer/export/${encodeURIComponent(sig)}`,
        { headers, responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `miss-${sig.replace(/::/g, "_").replace(/\//g, "_")}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Export failed");
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));

  return (
    <Card
      className="bg-gradient-to-br from-black via-neutral-950 to-black border-amber-500/40"
      data-testid="miss-analyzer-panel"
    >
      <CardContent className="p-5 space-y-5">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-rose-500 flex items-center justify-center">
              <Microscope className="w-5 h-5 text-black" />
            </div>
            <div>
              <div className="text-lg font-bold text-amber-100 tracking-tight">
                Miss Analyzer
                <span className="ml-2 text-[10px] text-amber-500/70 uppercase tracking-widest">
                  self-learning · 3-season study
                </span>
              </div>
              <div className="text-xs text-amber-500/70">
                Every miss dissected against form, H2H, in-game shocks and league density.
                The engine learns and stops repeating the same mistake.
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="bg-black border border-amber-500/40 text-amber-100 text-xs rounded px-2 py-1"
              data-testid="miss-days-select"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last 12 months</option>
            </select>
            <select
              value={verdict}
              onChange={(e) => setVerdict(e.target.value)}
              className="bg-black border border-amber-500/40 text-amber-100 text-xs rounded px-2 py-1"
              data-testid="miss-verdict-filter"
            >
              <option value="">All verdicts</option>
              {Object.entries(VERDICT_META).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            <Button
              size="sm"
              variant="outline"
              onClick={load}
              disabled={loading}
              className="text-amber-200 border-amber-500/40"
              data-testid="miss-reload-btn"
            >
              <RefreshCw className={`w-3 h-3 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            {isAdmin && (
              <Button
                size="sm"
                onClick={runAnalyzer}
                disabled={running}
                className="bg-gradient-to-r from-amber-500 to-rose-500 text-black font-bold"
                data-testid="miss-run-analyzer-btn"
              >
                <Search className={`w-3 h-3 mr-2 ${running ? "animate-pulse" : ""}`} />
                {running ? "Analyzing…" : "Run analyzer"}
              </Button>
            )}
          </div>
        </div>

        {error && (
          <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/40 rounded p-2"
               data-testid="miss-error">
            {error}
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <Card className="bg-black/50 border-amber-500/30">
              <CardContent className="p-0">
                <div className="px-4 py-3 border-b border-amber-500/20 flex items-center gap-2">
                  <Layers className="w-4 h-4 text-amber-400" />
                  <div className="text-sm font-bold text-amber-200 uppercase tracking-widest">
                    Recent misses ({misses.length})
                  </div>
                </div>
                <div className="max-h-[720px] overflow-y-auto" data-testid="miss-list-scroller">
                  {misses.length === 0 ? (
                    <div className="p-6 text-center text-xs text-amber-500/60">
                      {loading ? "Loading…" : "No misses in this window. Try increasing the range or running the analyzer."}
                    </div>
                  ) : (
                    misses.map((m) => (
                      <MissRow
                        key={m.pick_signature}
                        miss={m}
                        expanded={!!expanded[m.pick_signature]}
                        onToggle={() => toggle(m.pick_signature)}
                        onExport={exportOne}
                      />
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-4">
            <RollupCard rollups={rollups} />
            <LearningsCard learnings={learnings} pitfalls={pitfalls} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
